import { ReactNode, Suspense, useState, useEffect, useCallback } from "react";
import { useAtomValue } from "jotai";
import {
  DefaultNodeData,
  interfacePageAsyncAtomFamily,
  interfaceProjectAsyncAtomFamily,
  interfaceProjectPagesAsyncAtomFamily,
  hasZslAtom,
  tableFieldsAsyncAtomFamily,
  nodeViewModeAtom,
  aiSuggestedEditsAtomFamily,
} from "@zapier/canvas-state";

import { ToggleStepDetailsButton } from "../ToggleStepDetailsButton";
import { TableDetails } from "./TableDetails";
import { ProjectDetails } from "./InterfaceDetails/ProjectDetails";
import { PageDetails } from "./InterfaceDetails/PageDetails";
import styles from "./StepDetails.module.css";

interface Props {
  nodeId: string;
  proposalId: DefaultNodeData["proposalId"];
  tableId: DefaultNodeData["tableId"];
  interfaceProjectId: DefaultNodeData["projectId"];
  interfacePageId: DefaultNodeData["pageId"];
}

type ExpansionHandlerProps = {
  children: ReactNode;
};

const ExpansionHandler = ({ children }: ExpansionHandlerProps) => {
  const nodeViewMode = useAtomValue(nodeViewModeAtom);
  // Initialize local state based on global setting
  const [localExpanded, setLocalExpanded] = useState(
    nodeViewMode === "expanded",
  );

  // When global setting changes, update local state
  useEffect(() => {
    setLocalExpanded(nodeViewMode === "expanded");
  }, [nodeViewMode]);

  // Local state can always override global state
  const handleExpansionToggle = useCallback((newState: boolean) => {
    setLocalExpanded(newState);
  }, []);

  if (!localExpanded) {
    return (
      <div className={styles.toggleButtonWrapper}>
        <ToggleStepDetailsButton
          isExpanded={localExpanded}
          setIsExpanded={handleExpansionToggle}
        />
      </div>
    );
  }

  return (
    <>
      {children}
      <div className={styles.toggleButtonWrapper}>
        <ToggleStepDetailsButton
          isExpanded={localExpanded}
          setIsExpanded={handleExpansionToggle}
        />
      </div>
    </>
  );
};
ExpansionHandler.displayName = "ExpansionHandler";

function SuspendedTableDetails({
  nodeId,
  tableId,
  proposalId,
}: Pick<Props, "nodeId" | "tableId" | "proposalId">) {
  const hasZsl = useAtomValue(hasZslAtom);
  const tableFields = useAtomValue(
    tableFieldsAsyncAtomFamily({ nodeId, tableId, proposalId }),
  );
  const aiSuggestedEdits = useAtomValue(aiSuggestedEditsAtomFamily(nodeId));

  const isLoadingOutline = () => {
    const aiProposed = aiSuggestedEdits?.additions.find(
      (addition) => addition.id === proposalId,
    );
    return (
      aiProposed &&
      proposalId &&
      (!aiProposed.outline || Object.keys(aiProposed.outline).length === 0)
    );
  };

  const isDisabled = () => {
    return !aiSuggestedEdits?.additions.find(
      (addition) => addition.id === proposalId && addition.outline,
    );
  };

  if (!tableFields) {
    if (hasZsl) {
      return null;
    }

    return (
      <div className={styles.toggleButtonWrapper}>
        <ToggleStepDetailsButton
          isLoading={Boolean(isLoadingOutline())}
          isExpanded={false}
          isDisabled={isDisabled()}
          assetType="table"
        />
      </div>
    );
  }

  return (
    <ExpansionHandler>
      <TableDetails fields={tableFields} />
    </ExpansionHandler>
  );
}

const SuspendedInterfaceDetails = ({
  nodeId,
  interfaceProjectId,
  proposalId,
}: Pick<Props, "nodeId" | "interfaceProjectId" | "proposalId">) => {
  const hasZsl = useAtomValue(hasZslAtom);
  const aiSuggestedEdits = useAtomValue(aiSuggestedEditsAtomFamily(nodeId));

  const interfaceProject = useAtomValue(
    interfaceProjectAsyncAtomFamily({ nodeId, interfaceProjectId, proposalId }),
  );
  const interfacePages = useAtomValue(
    interfaceProjectPagesAsyncAtomFamily({
      nodeId,
      interfaceProjectId,
      proposalId,
    }),
  );

  const isLoadingOutline = () => {
    const aiProposed = aiSuggestedEdits?.additions.find(
      (addition) => addition.id === proposalId,
    );
    return (
      aiProposed &&
      proposalId &&
      (!aiProposed.outline || Object.keys(aiProposed.outline).length === 0)
    );
  };

  const isDisabled = () => {
    return !aiSuggestedEdits?.additions.find(
      (addition) => addition.id === proposalId && addition.outline,
    );
  };

  if (!interfaceProject) {
    if (hasZsl) {
      return null;
    }

    return (
      <div className={styles.toggleButtonWrapper}>
        <ToggleStepDetailsButton
          isLoading={Boolean(isLoadingOutline())}
          isExpanded={false}
          isDisabled={isDisabled()}
          assetType="interface"
        />
      </div>
    );
  }

  return (
    <ExpansionHandler
      // force a re-render or else some templates have same project id and the step will stay expanded when you select a different template
      key={`${interfaceProject.name}-${interfaceProject.id}`}
    >
      <ProjectDetails project={interfaceProject} pages={interfacePages} />
    </ExpansionHandler>
  );
};
SuspendedInterfaceDetails.displayName = "SuspendedInterfaceDetails";

const SuspendedInterfacePageDetails = ({
  nodeId,
  interfaceProjectId,
  interfacePageId,
  proposalId,
}: Pick<
  Props,
  "nodeId" | "interfaceProjectId" | "interfacePageId" | "proposalId"
>) => {
  const hasZsl = useAtomValue(hasZslAtom);
  const aiSuggestedEdits = useAtomValue(aiSuggestedEditsAtomFamily(nodeId));

  const interfaceProject = useAtomValue(
    interfaceProjectAsyncAtomFamily({ nodeId, interfaceProjectId, proposalId }),
  );
  const interfacePage = useAtomValue(
    interfacePageAsyncAtomFamily({
      nodeId,
      interfaceProjectId,
      interfacePageId,
      proposalId,
    }),
  );

  const isLoadingOutline = () => {
    const aiProposed = aiSuggestedEdits?.additions.find(
      (addition) => addition.id === proposalId,
    );
    return (
      aiProposed &&
      proposalId &&
      (!aiProposed.outline || Object.keys(aiProposed.outline).length === 0)
    );
  };

  const isDisabled = () => {
    return !aiSuggestedEdits?.additions.find(
      (addition) => addition.id === proposalId && addition.outline,
    );
  };
  if (!interfaceProject || !interfacePage) {
    if (hasZsl) {
      return null;
    }

    return (
      <div className={styles.toggleButtonWrapper}>
        <ToggleStepDetailsButton
          isLoading={Boolean(isLoadingOutline())}
          isExpanded={false}
          isDisabled={isDisabled()}
          assetType="interface"
        />
      </div>
    );
  }
  return (
    <ExpansionHandler
      // force a re-render or else some templates have same project and page id and the step will stay expanded when you select a different template
      key={`${interfaceProject.name}-${interfaceProject.id}-${interfacePage.id}`}
    >
      <PageDetails
        project={interfaceProject}
        page={interfacePage}
        proposalId={proposalId}
      />
    </ExpansionHandler>
  );
};
SuspendedInterfacePageDetails.displayName = "SuspendedInterfacePageDetails";

const SuspendedStepDetailsController = ({
  nodeId,
  tableId,
  proposalId,
  interfaceProjectId,
  interfacePageId,
}: Props) => {
  if (tableId) {
    return (
      <SuspendedTableDetails
        nodeId={nodeId}
        tableId={tableId}
        proposalId={proposalId}
      />
    );
  }

  if (interfacePageId) {
    return (
      <SuspendedInterfacePageDetails
        nodeId={nodeId}
        interfaceProjectId={interfaceProjectId}
        interfacePageId={interfacePageId}
        proposalId={proposalId}
      />
    );
  }

  if (interfaceProjectId) {
    return (
      <SuspendedInterfaceDetails
        nodeId={nodeId}
        interfaceProjectId={interfaceProjectId}
        proposalId={proposalId}
      />
    );
  }

  return null;
};
SuspendedStepDetailsController.displayName = "SuspendedStepDetailsController";

function LoadingButton() {
  return (
    <div className={styles.toggleButtonWrapper}>
      <ToggleStepDetailsButton isLoading={true} isExpanded={false} />
    </div>
  );
}

export function StepDetails(prop: Props) {
  return (
    <Suspense fallback={<LoadingButton />}>
      <SuspendedStepDetailsController {...prop} />
    </Suspense>
  );
}
